import { StoreFacetStore } from "@CommonApi/merchflow/store-facets";
import {
  ALL_BAYS_SHELVES,
  DraftGroup,
  DraftRule,
  isEmptyStoreFilter,
  matchesStoreFilter,
  parseStoreCodes,
} from "./types";

/** Why the backend's aggregated restriction sets cannot be satisfied. */
export type ConflictReason = "REQUIRED_AND_FORBIDDEN" | "OUTSIDE_ALLOWED_SET";

export interface ConflictSide {
  groupIndex: number;
  rule: DraftRule;
}

/** Which of the two rules in a pair a side refers to. */
export type ConflictSideKey = "a" | "b";

/** The two sides of a conflict, in display order. */
export const CONFLICT_SIDES: readonly ConflictSideKey[] = ["a", "b"];

export interface RuleConflict {
  /** Stable identity for React keys: the two rule ids. */
  key: string;
  reason: ConflictReason;
  /** The variant (as the user wrote it) that triggers the contradiction. */
  variant: string;
  a: ConflictSide;
  b: ConflictSide;
}

interface IndexedRule extends ConflictSide {
  order: number;
  stores: Set<string> | null;
  bays: Set<string> | null;
  shelf: string | null;
}

interface VariantContributor {
  entry: IndexedRule;
  variant: string;
}

const UNKNOWN_STORE = "__unknown-store__";
const ANY_BAY = "__any-bay__";
const ANY_SHELF = "__any-shelf__";

/** `variant` holds one or more variants separated by "|" (backend grammar). */
const splitVariants = (variant: string): string[] =>
  variant
    .split("|")
    .map((token) => token.trim())
    .filter(Boolean);

const variantKey = (token: string) => token.toLowerCase();

const isAllToken = (token: string) => {
  const normalized = token.trim().toLowerCase();
  return normalized === ALL_BAYS_SHELVES || normalized === "all";
};

/** `bay_number_rule` holds one or more numeric bays separated by "|". */
const bayKeys = (bayRule: string): Set<string> | null => {
  const tokens = bayRule
    .split(/[|,]/)
    .map((token) => token.trim())
    .filter(Boolean);
  if (tokens.some(isAllToken)) return null;
  return new Set(
    tokens.map((token) => {
      const numeric = Number(token);
      return Number.isFinite(numeric) ? String(numeric) : token.toLowerCase();
    }),
  );
};

/**
 * Canonical shelf target. Top positions are absolute in the backend (`Top` is
 * shelf 1), while Bottom positions depend on each bay's unknown shelf count and
 * therefore remain symbolic. null is the all-shelves wildcard.
 */
const shelfKey = (shelfRule: string): string | null => {
  if (isAllToken(shelfRule)) return null;
  const compact = shelfRule.toLowerCase().replace(/\s+/g, "");
  if (compact === "top") return "1";
  const fromTop = /^top\+(\d+)$/.exec(compact);
  if (fromTop) return String(1 + Number(fromTop[1]));
  if (compact === "bottom" || compact === "bottom-0") return "bottom";
  const fromBottom = /^bottom-(\d+)$/.exec(compact);
  if (fromBottom) return `bottom-${Number(fromBottom[1])}`;
  const numeric = Number(compact);
  return Number.isFinite(numeric) && compact !== "" ? String(numeric) : compact;
};

const resolveStores = (
  group: DraftGroup,
  facetStores: StoreFacetStore[] | null,
): Set<string> | null => {
  const rawCodes = parseStoreCodes(group.storeInput);
  const filterActive = !isEmptyStoreFilter(group.filter);

  // With no facets, an all-store or filtered scope is unknowable. Treat it as
  // applying to every candidate so potential conflicts remain visible.
  if (!facetStores) {
    return rawCodes.length === 0 || filterActive ? null : new Set(rawCodes);
  }

  if (rawCodes.length === 0 && !filterActive) {
    return new Set(facetStores.map((store) => store.code));
  }

  const known = new Set(facetStores.map((store) => store.code));
  const codes = new Set(rawCodes.filter((code) => known.has(code)));
  if (filterActive) {
    for (const store of facetStores) {
      if (matchesStoreFilter(store, group.filter)) codes.add(store.code);
    }
  }
  return codes;
};

/** A rule missing its variant, bay, or shelf isn't comparable yet. */
const isComplete = (rule: DraftRule) =>
  Boolean(rule.variant.trim() && rule.bayNumberRule.trim() && rule.shelfRule.trim());

const candidateStores = (
  entries: IndexedRule[],
  facetStores: StoreFacetStore[] | null,
): string[] => {
  if (facetStores) return facetStores.map((store) => store.code);
  const stores = new Set<string>();
  let needsUnknown = false;
  for (const entry of entries) {
    if (entry.stores === null) needsUnknown = true;
    else for (const store of entry.stores) stores.add(store);
  }
  if (needsUnknown || stores.size === 0) stores.add(UNKNOWN_STORE);
  return [...stores];
};

const candidateBays = (entries: IndexedRule[]): string[] => {
  const bays = new Set<string>();
  let needsAny = false;
  for (const entry of entries) {
    if (entry.bays === null) needsAny = true;
    else for (const bay of entry.bays) bays.add(bay);
  }
  if (needsAny || bays.size === 0) bays.add(ANY_BAY);
  return [...bays];
};

const candidateShelves = (entries: IndexedRule[]): string[] => {
  const shelves = new Set<string>();
  let needsAny = false;
  for (const entry of entries) {
    if (entry.shelf === null) needsAny = true;
    else shelves.add(entry.shelf);
  }
  if (needsAny || shelves.size === 0) shelves.add(ANY_SHELF);
  return [...shelves];
};

const addContributor = (
  contributors: Map<string, VariantContributor[]>,
  entry: IndexedRule,
  variant: string,
) => {
  const key = variantKey(variant);
  contributors.set(key, [...(contributors.get(key) ?? []), { entry, variant }]);
};

/** Aggregate one target exactly as the backend builds must/must-not/may sets. */
const conflictsAtTarget = (
  entries: IndexedRule[],
  addConflict: (
    first: VariantContributor,
    second: VariantContributor,
    reason: ConflictReason,
    variant: string,
  ) => void,
) => {
  const must = new Map<string, VariantContributor[]>();
  const mustNot = new Map<string, VariantContributor[]>();
  const may = new Set<string>();
  const mayContributors = new Map<string, VariantContributor>();
  let mayHaveAll = false;

  for (const entry of entries) {
    for (const variant of splitVariants(entry.rule.variant)) {
      switch (entry.rule.condition) {
        case "MUST_HAVE":
          addContributor(must, entry, variant);
          break;
        case "MUST_NOT_HAVE":
          addContributor(mustNot, entry, variant);
          break;
        case "CAN_HAVE":
          may.add(variantKey(variant));
          mayContributors.set(entry.rule.id, { entry, variant });
          if (variant.trim() === ALL_BAYS_SHELVES) mayHaveAll = true;
          break;
        case "MUST_HAVE_ONLY":
          addContributor(must, entry, variant);
          may.add(variantKey(variant));
          mayContributors.set(entry.rule.id, { entry, variant });
          if (variant.trim() === ALL_BAYS_SHELVES) mayHaveAll = true;
          break;
      }
    }
  }

  for (const [key, required] of must) {
    for (const requiredBy of required) {
      for (const forbiddenBy of mustNot.get(key) ?? []) {
        addConflict(requiredBy, forbiddenBy, "REQUIRED_AND_FORBIDDEN", requiredBy.variant);
      }

      if (!mayHaveAll && mayContributors.size > 0 && !may.has(key)) {
        for (const allowedBy of mayContributors.values()) {
          addConflict(requiredBy, allowedBy, "OUTSIDE_ALLOWED_SET", requiredBy.variant);
        }
      }
    }
  }
};

/** Find pairs participating in an unsatisfiable aggregate on one target. */
export const findRuleConflicts = (
  groups: DraftGroup[],
  facetStores: StoreFacetStore[] | null,
): RuleConflict[] => {
  let order = 0;
  const entries: IndexedRule[] = groups.flatMap((group, groupIndex) => {
    const stores = resolveStores(group, facetStores);
    return group.rules.filter(isComplete).map((rule) => ({
      groupIndex,
      rule,
      order: order++,
      stores,
      bays: bayKeys(rule.bayNumberRule),
      shelf: shelfKey(rule.shelfRule),
    }));
  });
  const conflicts = new Map<string, RuleConflict>();

  const addConflict = (
    first: VariantContributor,
    second: VariantContributor,
    reason: ConflictReason,
    variant: string,
  ) => {
    if (first.entry.rule.id === second.entry.rule.id) return;
    const [a, b] =
      first.entry.order < second.entry.order
        ? [first.entry, second.entry]
        : [second.entry, first.entry];
    const key = `${a.rule.id}~${b.rule.id}`;
    if (!conflicts.has(key)) {
      conflicts.set(key, {
        key,
        reason,
        variant,
        a: { groupIndex: a.groupIndex, rule: a.rule },
        b: { groupIndex: b.groupIndex, rule: b.rule },
      });
    }
  };

  for (const store of candidateStores(entries, facetStores)) {
    const storeEntries = entries.filter(
      (entry) => entry.stores === null || entry.stores.has(store),
    );
    for (const bay of candidateBays(storeEntries)) {
      const bayEntries = storeEntries.filter((entry) => entry.bays === null || entry.bays.has(bay));
      for (const shelf of candidateShelves(bayEntries)) {
        const shelfEntries = bayEntries.filter(
          (entry) => entry.shelf === null || entry.shelf === shelf,
        );
        conflictsAtTarget(shelfEntries, addConflict);
      }
    }
  }

  return [...conflicts.values()];
};

/** Ids of every rule involved in at least one conflict, for row highlighting. */
export const conflictedRuleIds = (conflicts: RuleConflict[]): Set<string> => {
  const ids = new Set<string>();
  for (const conflict of conflicts) {
    ids.add(conflict.a.rule.id);
    ids.add(conflict.b.rule.id);
  }
  return ids;
};
